import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import TimePicker from '@atlaskit/datetime-picker/time-picker';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';

import { times } from '../../../../../../../../common/constants';
import { asLocalTimeValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const styles = cssMap({
	container: {
		maxWidth: '160px',
	},
});

export const EditLocalTimeInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue);
	const isInvalid = useFieldInvalid({ fieldId });

	const currentValue = useCurrentValue({ fieldId });
	const formField = useFormField({ fieldId });
	const { updateModifiedValue } = useFormRendererActions();
	const intl = useIntl();
	const isRequired = useFieldRequired({ fieldId });
	const timePlaceholder = intl.formatMessage(messages.timePlaceholder, {
		time: intl.formatTime(new Date(), {}),
	});
	const onChange = useCallback(
		(value: string) => {
			if (value.length === 0) {
				updateModifiedValue(fieldId, null);
			} else {
				updateModifiedValue(fieldId, { localTime: value });
			}
		},
		[fieldId, updateModifiedValue],
	);

	return (
		<Box xcss={styles.container}>
			<TimePicker
				id={fieldInputId}
				value={asLocalTimeValue(currentValue)?.localTime}
				isInvalid={isInvalid}
				onChange={onChange}
				locale={intl.locale}
				placeholder={timePlaceholder}
				testId={`field-input-local-time-edit-${fieldId}`}
				timeIsEditable
				times={times}
				isRequired={isRequired}
				label={formField?.label || timePlaceholder}
				aria-describedby={
					isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
				}
			/>
		</Box>
	);
};
